// Persistent connectivity banner. Shows when the browser is offline or when
// our /api/* calls are stuck retrying. Auto-dismisses with a brief "Back online"
// toast when connectivity returns.

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { subscribeNetworkStatus } from "../lib/network";

export default function OfflineToast() {
  const [status, setStatus] = useState({ online: true, apiFailing: false });
  const [showRecovered, setShowRecovered] = useState(false);

  useEffect(() => {
    let prevBad = false;
    const unsub = subscribeNetworkStatus((s) => {
      const bad = !s.online || s.apiFailing;
      // Edge: bad → good. Pop a brief "Back online" confirmation.
      if (prevBad && !bad) {
        setShowRecovered(true);
        setTimeout(() => setShowRecovered(false), 2400);
      }
      prevBad = bad;
      setStatus(s);
    });
    return unsub;
  }, []);

  const offline = !status.online;
  const apiFailing = status.online && status.apiFailing;
  const visible = offline || apiFailing;

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="fixed left-1/2 z-[140] glass rounded-full px-4 py-2 flex items-center gap-2.5"
            style={{
              top: "calc(env(safe-area-inset-top, 0px) + 12px)",
              transform: "translateX(-50%)",
              border: `1px solid ${offline ? "rgba(255,92,92,0.55)" : "rgba(255,215,0,0.55)"}`,
              boxShadow: `0 0 20px ${offline ? "rgba(255,92,92,0.30)" : "rgba(255,215,0,0.30)"}`,
              maxWidth: "calc(100vw - 32px)",
            }}
            data-testid={offline ? "offline-toast" : "api-retry-toast"}
            role="status"
            aria-live="polite"
          >
            <span
              className="rounded-full"
              style={{
                width: 8, height: 8,
                background: offline ? "#FF5C5C" : "#FFD700",
                boxShadow: `0 0 8px ${offline ? "#FF5C5C" : "#FFD700"}`,
                animation: "pulse 1.4s ease-in-out infinite",
              }}
            />
            <span className="font-display font-bold text-xs whitespace-nowrap">
              {offline ? "You're offline" : "Reconnecting…"}
            </span>
            <span className="text-white/55 text-[11px] hidden sm:inline">
              {offline ? "Game will resume when you're back" : "Last action will retry automatically"}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showRecovered && !visible && (
          <motion.div
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="fixed left-1/2 z-[140] glass rounded-full px-4 py-2 flex items-center gap-2.5"
            style={{
              top: "calc(env(safe-area-inset-top, 0px) + 12px)",
              transform: "translateX(-50%)",
              border: "1px solid rgba(61,220,151,0.55)",
              boxShadow: "0 0 20px rgba(61,220,151,0.30)",
            }}
            data-testid="online-recovered-toast"
            role="status"
            aria-live="polite"
          >
            <span
              className="rounded-full"
              style={{ width: 8, height: 8, background: "#3DDC97", boxShadow: "0 0 8px #3DDC97" }}
            />
            <span className="font-display font-bold text-xs whitespace-nowrap">Back online</span>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
